import React, { useMemo, useState, useEffect } from "react";
import "./styles.css";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  limit,
  query,
} from "firebase/firestore";
import { database } from "./firebaseConfig";
import FilterComponent from "./EditFilterProps";
import EditFloraDisplayData from "./EditFloraDisplayData";
import { useLanguage } from "./languageContext";

interface RowData {
  id: string
  Estacion: string | null | undefined
  Fecha: string | null | undefined
  Hora: string | null | undefined
  X: string | null | undefined
  Y: string | null | undefined
  CategoriaAmenaza: string | null | undefined
  Fenologia: string | null | undefined
  NombreCientifico: string | null | undefined
  NombreComun: string | null | undefined
  NumeroIndividuos: string | null | undefined
  IngestionDate: string
}

function EditFloraTable() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedNMater, setSelectedNMater] = useState<string[]>([]);
  const [selectedEstation, setSelectedEstation] = useState<string[]>([]);
  const [selectedMaterialGroup, setSelectedMaterialGroup] = useState<string[]>(
    []
  );

  const [data, setdata] = useState<RowData[]>([]);
  const ref = collection(database, "Flora");
  const [refresh, setrefresh] = useState(true);

  useEffect(() => {
    if (refresh) {
      getUsers();
      setrefresh(false);
    }
  }, [refresh]);

  const handleRefresh = () => {
    setrefresh(true);
  };

  // TODO:change function name
  const getUsers = async () => {
    try {
      const q = query(collection(database, "Flora"), limit(100));
      const data = await getDocs(q);
      setdata(
        data.docs.map((doc) => ({ ...(doc.data() as RowData), id: doc.id }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const filteredData = useMemo(() => {
    let filtered = data;

    if (startDate && endDate) {
      filtered = filtered.filter((entry) => {
        const entryDate = new Date(entry.IngestionDate);
        return (
          entryDate >= new Date(startDate) && entryDate <= new Date(endDate)
        );
      });
    }

    return filtered;
  }, [
    data,
    startDate,
    endDate,
    selectedNMater,
    selectedEstation,
    selectedMaterialGroup,
  ]);

  //TODO: handle convert to CSV
  function convertToCSV(filteredData) {
    const header = Object.keys(data[0]).join(",") + "\n";
    const rows = data.map((row) => Object.values(row).join(",") + "\n");
    return header + rows.join("");
  }
  
  function handleDownloadCSV() {
    const csvData = convertToCSV(filteredData);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    const currentDate = new Date()
      .toISOString()
      .replace(/[-:.]/g, "")
      .slice(0, 15);
    const filename = `Registro_${currentDate}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
  const { language, toggleLanguage } = useLanguage();

  const columns = useMemo(
    () => [
      { header: language === "english" ? "ID" : "ID", header_eng: "ID", header_esp: "ID", accessorKey: "id", footer: "id", description_eng: "Unique identifier for the record", description_esp: "Identificador único para el registro" },
      { header: language === "english" ? "Station" : "Estación", header_eng: "Station", header_esp: "Estación", accessorKey: "Estacion", footer: "Estacion", description_eng: "Location of the station", description_esp: "Ubicación de la estación" },
      { header: language === "english" ? "Date" : "Fecha", header_eng: "Date", header_esp: "Fecha", accessorKey: "Fecha", footer: "Fecha", description_eng: "Date of the observation", description_esp: "Fecha de la observación" },
      { header: language === "english" ? "Time" : "Hora", header_eng: "Time", header_esp: "Hora", accessorKey: "Hora", footer: "Hora", description_eng: "Time of the observation", description_esp: "Hora de la observación" },
      { header: language === "english" ? "X" : "X", header_eng: "X", header_esp: "X", accessorKey: "X", footer: "X", description_eng: "X coordinate", description_esp: "Coordenada X" },
      { header: language === "english" ? "Y" : "Y", header_eng: "Y", header_esp: "Y", accessorKey: "Y", footer: "Y", description_eng: "Y coordinate", description_esp: "Coordenada Y" },
      { header: language === "english" ? "Threat Category" : "Categoría de Amenaza", header_eng: "Threat Category", header_esp: "Categoría de Amenaza", accessorKey: "CategoriaAmenaza", footer: "CategoriaAmenaza", description_eng: "Category of threat", description_esp: "Categoría de amenaza" },
      { header: language === "english" ? "Phenology" : "Fenología", header_eng: "Phenology", header_esp: "Fenología", accessorKey: "Fenologia", footer: "Fenologia", description_eng: "Phenological state", description_esp: "Estado fenológico" },
      { header: language === "english" ? "Scientific Name" : "Nombre Científico", header_eng: "Scientific Name", header_esp: "Nombre Científico", accessorKey: "NombreCientifico", footer: "NombreCientifico", description_eng: "Scientific name of the species", description_esp: "Nombre científico de la especie" },
      { header: language === "english" ? "Common Name" : "Nombre Común", header_eng: "Common Name", header_esp: "Nombre Común", accessorKey: "NombreComun", footer: "NombreComun", description_eng: "Common name of the species", description_esp: "Nombre común de la especie" },
      { header: language === "english" ? "Number of Individuals" : "Número de Individuos", header_eng: "Number of Individuals", header_esp: "Número de Individuos", accessorKey: "NumeroIndividuos", footer: "NumeroIndividuos", description_eng: "Number of individuals observed", description_esp: "Número de individuos observados" },
    ],
    [language]
  );


  const [showDescription, setShowDescription] = useState("");


  return (
    <>
      <div className="container">
        <div className="table-scroll">
          <div className="filters">
            <FilterComponent
              label={language === "english" ? "Start date" : "Fecha de inicio"}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <FilterComponent
              label={language === "english" ? "End date" : "Fecha final"}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            <FilterComponent
                  label={language === "english" ? "Select station" :"Seleccionar Estacion"}
                  value={selectedEstation}
                  onChange={(e) => setSelectedEstation(Array.from((e.target as HTMLSelectElement).selectedOptions, (option) => option.value))}
                  options={Array.from(new Set(data.map((entry) => entry.Estacion)))}
                  multiple
                />
            <div className="filter-select"></div>
          </div>
        </div>
      <EditFloraDisplayData
        data={filteredData}
        columns={columns.map(column => ({
          ...column,
          header: () => (
            <div 
              onMouseEnter={() => setShowDescription(column.accessorKey)} 
              onMouseLeave={() => setShowDescription(null)}
              style={{ position: 'relative' }}
            >
              {language==="english" ? column.header_eng : column.header_esp}
              {showDescription === column.accessorKey && (
                <div 
                  className="header-description-bubble" 
                  style={{
                    position: 'absolute',
                    left: '50%',
                    padding: '5px',
                    backgroundColor: 'white',
                    border: '1px solid green',
                    borderRadius: '5px',
                    zIndex: 2,
                  }}
                >
                  {language==="english" ? column.description_eng : column.description_esp}
                </div>
              )}
            </div>
          )
        }))}
              CollectionName="flora"
      />
      </div>



      <div className="button-container">
        <button className="refresh-button" onClick={handleRefresh}>
          {language === "english" ? "Refresh data" : "Actualizar datos"}
        </button>
        <button className="download-button" onClick={handleDownloadCSV}>
          {language === "english" ? "Download data" : "Descargar datos"}
        </button>
      </div>
    </>
  );
}

export default EditFloraTable;
